import React, { useState, useEffect } from "react";
import "./DisputeFilters.css";

export interface FilterState {
  dateFrom: string;
  dateTo: string;
  minConfidence: number;
  maxConfidence: number;
  statuses: string[];
  categories: string[];
  minAmount: string;
  maxAmount: string;
}

interface DisputeFiltersProps {
  onApply: (filters: FilterState) => void;
  availableCategories: string[];
  isOpen: boolean;
  onToggle: () => void;
}

const AVAILABLE_STATUSES = [
  "processed",
  "appealed",
  "complied",
  "non_compliant",
];

const DisputeFilters: React.FC<DisputeFiltersProps> = ({
  onApply,
  availableCategories,
  isOpen,
  onToggle,
}) => {
  const [filters, setFilters] = useState<FilterState>({
    dateFrom: "",
    dateTo: "",
    minConfidence: 0,
    maxConfidence: 100,
    statuses: [],
    categories: [],
    minAmount: "",
    maxAmount: "",
  });

  const handleConfidenceChange = (
    type: "min" | "max",
    value: string | number
  ) => {
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    setFilters((prev) => ({
      ...prev,
      [type === "min" ? "minConfidence" : "maxConfidence"]: numValue,
    }));
  };

  const handleStatusToggle = (status: string) => {
    setFilters((prev) => ({
      ...prev,
      statuses: prev.statuses.includes(status)
        ? prev.statuses.filter((s) => s !== status)
        : [...prev.statuses, status],
    }));
  };

  const handleCategoryToggle = (category: string) => {
    setFilters((prev) => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter((c) => c !== category)
        : [...prev.categories, category],
    }));
  };

  const handleApply = () => {
    onApply(filters);
    onToggle();
  };

  const handleClear = () => {
    const clearedFilters: FilterState = {
      dateFrom: "",
      dateTo: "",
      minConfidence: 0,
      maxConfidence: 100,
      statuses: [],
      categories: [],
      minAmount: "",
      maxAmount: "",
    };
    setFilters(clearedFilters);
    onApply(clearedFilters);
  };

  const hasActiveFilters = () => {
    return (
      filters.dateFrom !== "" ||
      filters.dateTo !== "" ||
      filters.minConfidence > 0 ||
      filters.maxConfidence < 100 ||
      filters.statuses.length > 0 ||
      filters.categories.length > 0 ||
      filters.minAmount !== "" ||
      filters.maxAmount !== ""
    );
  };

  const getActiveFiltersSummary = () => {
    const summary: { key: string; label: string }[] = [];

    if (filters.dateFrom || filters.dateTo) {
      const from = filters.dateFrom || "...";
      const to = filters.dateTo || "...";
      summary.push({ key: "date", label: `Date: ${from} – ${to}` });
    }

    if (filters.minConfidence > 0 || filters.maxConfidence < 100) {
      summary.push({
        key: "confidence",
        label: `Confidence: ${filters.minConfidence}% – ${filters.maxConfidence}%`,
      });
    }

    if (filters.minAmount || filters.maxAmount) {
      const min = filters.minAmount || "0";
      const max = filters.maxAmount || "∞";
      summary.push({ key: "amount", label: `Amount: $${min} – $${max}` });
    }

    filters.statuses.forEach((status) => {
      summary.push({
        key: `status-${status}`,
        label: `Status: ${status.replace("_", " ")}`,
      });
    });

    filters.categories.forEach((category) => {
      summary.push({
        key: `category-${category}`,
        label: `Category: ${category}`,
      });
    });

    return summary;
  };

  const removeFilter = (key: string) => {
    if (key === "date") {
      setFilters((prev) => ({ ...prev, dateFrom: "", dateTo: "" }));
    } else if (key === "confidence") {
      setFilters((prev) => ({ ...prev, minConfidence: 0, maxConfidence: 100 }));
    } else if (key === "amount") {
      setFilters((prev) => ({ ...prev, minAmount: "", maxAmount: "" }));
    } else if (key.startsWith("status-")) {
      const status = key.replace("status-", "");
      setFilters((prev) => ({
        ...prev,
        statuses: prev.statuses.filter((s) => s !== status),
      }));
    } else if (key.startsWith("category-")) {
      const category = key.replace("category-", "");
      setFilters((prev) => ({
        ...prev,
        categories: prev.categories.filter((c) => c !== category),
      }));
    }
  };

  return (
    <div className="dispute-filters-container">
      <button
        className={`filters-toggle-btn ${hasActiveFilters() ? "active" : ""}`}
        onClick={onToggle}
        type="button"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
        Advanced Filters
        {hasActiveFilters() && (
          <span className="active-filters-badge">
            {[
              filters.dateFrom || filters.dateTo ? 1 : 0,
              filters.minConfidence > 0 || filters.maxConfidence < 100 ? 1 : 0,
              filters.statuses.length,
              filters.categories.length,
              filters.minAmount || filters.maxAmount ? 1 : 0,
            ]
              .filter((n) => n > 0)
              .reduce((a, b) => a + b, 0)}
          </span>
        )}
        <svg
          className={`toggle-arrow ${isOpen ? "open" : ""}`}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div className="filters-panel">
          {/* Active Filters Summary */}
          {hasActiveFilters() && (
            <div className="active-filters-summary">
              {getActiveFiltersSummary().map((filter) => (
                <div key={filter.key} className="summary-chip">
                  {filter.label}
                  <button
                    className="summary-chip-close"
                    onClick={() => removeFilter(filter.key)}
                    type="button"
                    aria-label="Remove filter"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="filters-grid">
            {/* Row 1: Date, Confidence, Amount */}
            <div className="filters-row-1">
              {/* Date Range */}
              <div className="filter-group">
                <label className="filter-label">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect
                      x="3"
                      y="4"
                      width="18"
                      height="18"
                      rx="2"
                      ry="2"
                    ></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  Date Range
                </label>
                <div className="filter-date-inputs">
                  <input
                    type="date"
                    className="filter-input"
                    value={filters.dateFrom}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        dateFrom: e.target.value,
                      }))
                    }
                    placeholder="From"
                  />
                  <span className="date-separator">to</span>
                  <input
                    type="date"
                    className="filter-input"
                    value={filters.dateTo}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        dateTo: e.target.value,
                      }))
                    }
                    placeholder="To"
                  />
                </div>
              </div>

              {/* Confidence Range */}
              <div className="filter-group">
                <label className="filter-label">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M9 11l3 3L22 4"></path>
                    <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"></path>
                  </svg>
                  Confidence Range
                </label>
                <div className="confidence-range-container">
                  <div className="confidence-range-slider-wrapper">
                    <div
                      className="confidence-range-track"
                      style={{
                        left: `${filters.minConfidence}%`,
                        width: `${
                          filters.maxConfidence - filters.minConfidence
                        }%`,
                      }}
                    />
                    <input
                      type="range"
                      className="confidence-slider confidence-slider-min"
                      min="0"
                      max="100"
                      value={filters.minConfidence}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (value <= filters.maxConfidence) {
                          handleConfidenceChange("min", value);
                        }
                      }}
                    />
                    <input
                      type="range"
                      className="confidence-slider confidence-slider-max"
                      min="0"
                      max="100"
                      value={filters.maxConfidence}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (value >= filters.minConfidence) {
                          handleConfidenceChange("max", value);
                        }
                      }}
                    />
                  </div>
                  <div className="confidence-range-values">
                    <input
                      type="number"
                      className="confidence-input"
                      min="0"
                      max="100"
                      value={filters.minConfidence}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        if (value <= filters.maxConfidence) {
                          handleConfidenceChange("min", value);
                        }
                      }}
                    />
                    <span className="range-separator">—</span>
                    <input
                      type="number"
                      className="confidence-input"
                      min="0"
                      max="100"
                      value={filters.maxConfidence}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        if (value >= filters.minConfidence) {
                          handleConfidenceChange("max", value);
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Amount Range */}
              <div className="filter-group">
                <label className="filter-label">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"></path>
                  </svg>
                  Amount Range (USD)
                </label>
                <div className="filter-amount-inputs">
                  <input
                    type="number"
                    className="filter-input"
                    placeholder="Min"
                    value={filters.minAmount}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        minAmount: e.target.value,
                      }))
                    }
                    min="0"
                    step="0.01"
                  />
                  <span className="amount-separator">to</span>
                  <input
                    type="number"
                    className="filter-input"
                    placeholder="Max"
                    value={filters.maxAmount}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        maxAmount: e.target.value,
                      }))
                    }
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>

            {/* Row 2: Status Filter (Full Width) */}
            <div className="filter-group filter-group-full">
              <label className="filter-label">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 6v6l4 2"></path>
                </svg>
                Status
              </label>
              <div className="filter-checkboxes status-checkboxes">
                {AVAILABLE_STATUSES.map((status) => (
                  <label key={status} className="filter-checkbox-label">
                    <input
                      type="checkbox"
                      checked={filters.statuses.includes(status)}
                      onChange={() => handleStatusToggle(status)}
                    />
                    <span className="checkbox-text">
                      {status.replace("_", " ")}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Row 3: Category Filter (Full Width) */}
            {availableCategories.length > 0 && (
              <div className="filter-group filter-group-full">
                <label className="filter-label">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M4 4h7l2 2h7a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2z"></path>
                  </svg>
                  Categories
                </label>
                <div className="filter-checkboxes category-checkboxes">
                  {availableCategories.slice(0, 10).map((category) => (
                    <label key={category} className="filter-checkbox-label">
                      <input
                        type="checkbox"
                        checked={filters.categories.includes(category)}
                        onChange={() => handleCategoryToggle(category)}
                      />
                      <span className="checkbox-text">{category}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="filters-actions">
            <button
              className="filter-action-btn filter-clear-btn"
              onClick={handleClear}
              type="button"
            >
              Clear All
            </button>
            <button
              className="filter-action-btn filter-apply-btn"
              onClick={handleApply}
              type="button"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisputeFilters;
